import { OrderStatus } from '../../constants/order-status';
import { CartGroup, CartGroupNameErrorMessage, CartItem, CartParams, CartSampling, UnavailableCartItem } from '../../model/cart.model';
import { Price } from '../../model/catalogue-category/price.model';
import { LimbusErrorResponse, ResponseMessage } from '../../model/limbus-error-response.model';
import * as CartActions from './cart.actions';
import { createRecommendedSuppliesAdapter } from './recommended-supplies.case-reducers';
import {
    LimbusErrorWithStatus,
    addRecommendedSupplies,
    addSamplingSupplyGroupId,
    buildLimbusErrorResponseWithStatus,
    getAddingItemsToCartWithout,
    getSamplingSupplyGroupItems,
    getServiceGroupItems,
    setCartUpdateError,
    setSamplingGroupErrorMessage,
    setSamplingSuppliesSampling,
    setUnavailableCartItems,
    sumGroupItems,
    sumGroupUnavailableItems,
    unavailableCartItemsPresent,
    updatedGroupWithUnavailableItems
} from '@app/store/cart/cart.reducers.utils';

export interface State {
    loading: boolean;
    cartId: string;
    cartUpdateError: {
        error: string;
        groupId: number;
        itemId: number;
    };
    cartUpdated: boolean;
    cartPriceUpdated: boolean;
    limbusErrorResponse: LimbusErrorResponse;
    groups: CartGroup[];
    samplingSuppliesGroupId: number;
    cartPrice: Price;
    orderStatus: OrderStatus;
    samplingSuppliesSampling: CartSampling;
    cartParams: CartParams;
    unavailableCartItems: UnavailableCartItem[];
    isCartEmpty: boolean;
    isAddingAnalyticalTest: boolean;
    addingItemToCart: { number?: number }; // Adding these map of items to the cart is in progress. Help preventing
    // duplicate items added to the cart when network is slow
    isPlacingOrder: boolean;
    recommendSupplies: CartItem[];
}

const initialState = {
    loading: false,
    cartId: '',
    cartUpdateError: null,
    cartUpdated: false,
    cartPriceUpdated: false,
    limbusErrorResponse: null,
    groups: [],
    samplingSuppliesGroupId: null,
    cartPrice: null,
    orderStatus: OrderStatus.NOTHING,
    samplingSuppliesSampling: null,
    cartParams: { analyticalTest: null, groups: [] },
    unavailableCartItems: [],
    isCartEmpty: true,
    isAddingAnalyticalTest: null,
    addingItemToCart: {},
    isPlacingOrder: false,
    recommendSupplies: []
};

const recommendSuppliesAdapter = createRecommendedSuppliesAdapter();


export function CartReducer(
    state = initialState,
    action: CartActions.CartActions) {

    switch (action.type) {

    case CartActions.RELOAD_CART:
        return { ...state, loading: true };
    case CartActions.GET_CART_SUCCESS:
        return { ...state, loading: false };
    case CartActions.GET_CART_FAIL:
        return { ...state, loading: false };
    case CartActions.CREATE_CART:
        return {
            ...state,
            cartId: action.cart.id
        };
    case CartActions.SET_IS_ADDING_ANALYTICAL_TEST:
        return { ...state, isAddingAnalyticalTest: action.state, cartUpdateError: action.state ? null : state.cartUpdateError };
    case CartActions.UPDATE_CART:
        const { groups, cartPrice, id } = action.payload;
        const numOfItems = sumGroupItems(groups) + sumGroupUnavailableItems(groups);
        const stateNumOfItems = sumGroupItems(state.groups) + sumGroupUnavailableItems(state.groups);
        const nonEmptyCart: boolean = (numOfItems !== 0);
        const priceUpdateFlag: boolean = ((numOfItems === 0) && (state.cartPrice != null)) ||
          ((numOfItems > 0) && ((state.cartPrice == null) ||
              (stateNumOfItems !== numOfItems) ||
              (state.cartPrice.amount !== cartPrice.amount)));
        const itemsChanged = numOfItems !== stateNumOfItems;
        return {
            ...state,
            cartId: id,
            groups: updatedGroupWithUnavailableItems(groups),
            cartUpdated: itemsChanged,
            cartPriceUpdated: priceUpdateFlag,
            limbusErrorResponse: null,
            cartUpdateError: setCartUpdateError(state.cartUpdateError, action.groupId),
            cartPrice,
            isCartEmpty: !nonEmptyCart,
            samplingSuppliesSampling: setSamplingSuppliesSampling(groups),
            unavailableCartItems: [...setUnavailableCartItems(groups)],
            addingItemToCart: !!action.itemId
                ? getAddingItemsToCartWithout(action.itemId, state.addingItemToCart)
                : state.addingItemToCart
        };
    case CartActions.UPDATE_CART_FAIL:
        return {
            ...state,
            cartUpdateError: { error: action.error, groupId: action.groupId, itemId: action.itemId },
            addingItemToCart: {}
        };
    case CartActions.SET_GROUP_ERRROR_MESSAGE:
        let errorMessage: CartGroupNameErrorMessage = null;
        if (action.errorResponse != null) {
            // For now considering first error message, as multiple error messages is a not regular scenario here
            errorMessage = new CartGroupNameErrorMessage(action.errorResponse.error.messages[0].message, action.userInputGroupName);
        }
        return {
            ...state,
            groups: setSamplingGroupErrorMessage(state.groups, action.groupId, errorMessage)
        };
    case CartActions.SET_ORDER_STATUS:
        const updatedErrorResponse: LimbusErrorResponse = (action.value === OrderStatus.IN_PROGRESS) ? null : state.limbusErrorResponse;
        return {
            ...state,
            orderStatus: action.value,
            limbusErrorResponse: updatedErrorResponse
        };
    case CartActions.CLEAR_LIMBUS_ERROR_RESPONSE:
        return {
            ...state,
            limbusErrorResponse: null,
            orderStatus: OrderStatus.NOTHING
        };
    case CartActions.SET_LIMBUS_ERROR_RESPONSE:
        return {
            ...state,
            limbusErrorResponse: action.errors
        };
    case CartActions.PLACE_ORDER:
        return { ...state, isPlacingOrder: false };
    case CartActions.PLACE_ORDER_SUCCESS:

        return {
            ...state,
            cartId: action.payload.id,
            groups: [...action.payload.groups],
            cartUpdated: false,
            isCartEmpty: true,
            cartPriceUpdated: false,
            limbusErrorResponse: null,
            cartUpdateError: null,
            cartPrice: action.payload.cartPrice,
            orderStatus: OrderStatus.SUCCESS,
            samplingSuppliesSampling: setSamplingSuppliesSampling([]),
            isPlacingOrder: false
        };
    case CartActions.PLACE_ORDER_FAILURE:
        const errorWithStatus: LimbusErrorWithStatus = buildLimbusErrorResponseWithStatus(
            action.payload
        );
        return {
            ...state,
            cartUpdated: false,
            cartPriceUpdated: false,
            orderStatus: errorWithStatus.status,
            limbusErrorResponse: errorWithStatus.error,
            isPlacingOrder: false
        };
    case CartActions.SET_SAMPLING_SUPPLIES_GRP_ID:
        return {
            ...state,
            samplingSuppliesGroupId: action.group.id,
            groups: addSamplingSupplyGroupId(state.groups, action.group)
        };
    case CartActions.SET_SAMPLING_SUPPLIES_SAMPLING:
        return {
            ...state,
            samplingSuppliesSampling: action.sampling,
        };
    case CartActions.ADD_CART_PARAMS:
        const updatedGroups = action.cartParams.groups.map(group => ({ ...group }));
        return {
            ...state,
            cartUpdated: false,
            cartParams: { ...action.cartParams, groups: [...updatedGroups] }
        };
    case CartActions.RESET_CART_PARAMS:
        return {
            ...state,
            cartParams: null
        };
    case CartActions.ADD_CART_ITEM:
    case CartActions.UPDATE_CART_ITEM:
        return {
            ...state,
            cartUpdated: false,
            addingItemToCart: { ...state.addingItemToCart, [action.payload.item.id]: action.payload.item.id }
        };
    case CartActions.LOAD_RECOMMENDED_SUPPLIES:
        return recommendSuppliesAdapter.addAll(action.items, state);
    case CartActions.DELETE_CART_GROUP:
        return {
            ...state,
            cartUpdated: false,
            cartUpdateError: setCartUpdateError(state.cartUpdateError, action.groupId)
        };
    default:
        return state;
    }
}

// selectors
export const getSamplingSuppliesGroupId = (state: State) => state.samplingSuppliesGroupId;
export const getCartId = (state: State) => state.cartId;
export const getCartGroups = (state: State) => state.groups;
export const getIsCartEmpty = (state: State) => state.isCartEmpty;
export const getOrderStatus = (state: State) => state.orderStatus;
export const isCartItemUnavailable = (state: State) => unavailableCartItemsPresent(state.unavailableCartItems);
export const getSamplingSuppliesSampling = (state: State) => state.samplingSuppliesSampling;
export const getCartParams = (state: State) => state.cartParams;
export const getCartForCheckout = (state: State) => ({
    cartId: state.cartId,
    cartPrice: state.cartPrice,
    error: state.limbusErrorResponse });

export const getCheckoutError = (state: State) => state.limbusErrorResponse;

export const getCheckoutVoucherError = (state: State) => {
    if (!state.limbusErrorResponse) {
        return null;
    }

    return state.limbusErrorResponse.messages.filter(
        (msg: ResponseMessage) => msg.reference === 'voucherReference'
    );
};

export const getIsAddingAnalyticalTest = (state: State) => state.isAddingAnalyticalTest;
export const isCartPriceZero = (state: State) => state.isCartEmpty || (state.cartPrice.amount <= 0);
export const cartWithSamplingSupply = (state: State) => {
    const samplingSupplyGroup = state.groups.filter(group => (group.name === 'Sampling Supplies'));
    return state.groups.length > 0 && samplingSupplyGroup.length > 0 && samplingSupplyGroup[0].items.length > 0;
};
export const getCartPrice = (state: State) => state.cartPrice;
export const getCartUpdated = (state: State) => state.cartUpdated;

export const getCartServiceItems = (state: State) => getServiceGroupItems(state.groups);
export const getCartSamplingSupplyItems = (state: State) => addRecommendedSupplies(
    getSamplingSupplyGroupItems(state.groups),
    state
);
export const getCartRecommendedSupplies = (state: State) => state.recommendSupplies;


export const isCartLoading = (state: State) => state.loading;
export const isAddingItemToCartFn = (itemId) => (state: State) => !!state.addingItemToCart[itemId];
export const isAddingItemToCart = (state: State) => Object.keys(state.addingItemToCart).length > 0;
export const getAutoPopulatedCartItems = (state: State) => getSamplingSupplyGroupItems(state.groups)
    .filter((item: CartItem) => item.autoPopulatedQty && item.autoPopulatedQty > 0);
export const getNonAutoPopulatedCartItems = (state: State) => getSamplingSupplyGroupItems(state.groups)
    .filter((item: CartItem) => item.quantity && item.quantity > 0);
export const isPlacingOder = (state: State) => state.isPlacingOrder;
export const getCartUpdateError = (state: State) => state.cartUpdateError ? state.cartUpdateError.error : null;





